import { useSearch } from "@tanstack/react-router";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect } from "react";

type Theme = "light" | "dark" | "system";

const themeAtom = atomWithStorage<Theme>("theme", "system");

const getSystemTheme = (): "light" | "dark" => {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

const getEffectiveTheme = (theme: Theme): "light" | "dark" => {
  if (theme === "system") return getSystemTheme();
  return theme;
};

export const useTheme = () => {
  const [theme, setTheme] = useAtom(themeAtom);
  const search = useSearch({ strict: false }) as { theme?: string };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  const setSystemTheme = () => {
    setTheme("system");
  };

  useEffect(() => {
    const urlTheme = search?.theme;
    let effectiveTheme: "light" | "dark";

    if (urlTheme === "dark" || urlTheme === "light") {
      effectiveTheme = urlTheme;
    } else {
      effectiveTheme = getEffectiveTheme(theme);
    }

    document.documentElement.classList.toggle(
      "dark",
      effectiveTheme === "dark",
    );
  }, [theme, search]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      if (theme === "system" && !search?.theme) {
        const effectiveTheme = mediaQuery.matches ? "dark" : "light";
        document.documentElement.classList.toggle(
          "dark",
          effectiveTheme === "dark",
        );
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, search]);

  return {
    theme,
    setTheme,
    toggleTheme,
    setSystemTheme,
    isDark: getEffectiveTheme(theme) === "dark",
  };
};
